// Pozycja rejestru ośrodka ↔ wiersz tabeli rejestru (IssueLogGrid). Czyste funkcje.
import type { SiteIssue, SiteIssueState } from "../../api/siteIssuesApi";
import { ActionStatus } from "../../types/reportActions";
import type { ActionLogRow } from "../report/actionLog/actionLogFormat";

const day = (iso: string | null | undefined) => (iso ? iso.split("T")[0] : null);

export const rowFromIssue = (i: SiteIssue): ActionLogRow => ({
  key: i.id,
  id: i.id,
  participantNumber: i.participantNumber ?? "",
  visitNumber: i.visitNumber ?? "",
  categoryId: i.categoryId ?? null,
  deviationDescription: i.description ?? "",
  identificationDate: day(i.identificationDate),
  closedDate: day(i.closedDate),
  status: i.status ?? ActionStatus.Open,
  actionsTaken: i.actionsTaken ?? "",
  responsiblePerson: i.responsiblePerson ?? "",
  potentialDeviation: !!i.potentialDeviation,
  potentialSeriousFinding: !!i.serious,
  seriousBreach: !!i.seriousBreach,
  needsReview: !!i.needsReview,
  reviewNote: i.reviewNote,
});

// Pola, których tabela nie edytuje (osoba identyfikująca), bierzemy z zapisanej pozycji.
// Zamknięcie pozycji w tabeli zdejmuje oznaczenie „do weryfikacji”; inna edycja go nie rusza.
export const stateFromRow = (row: ActionLogRow, saved?: SiteIssue): SiteIssueState => ({
  participantNumber: row.participantNumber.trim(),
  visitNumber: row.visitNumber.trim() || null,
  categoryId: row.categoryId,
  description: row.deviationDescription.trim(),
  identificationDate: row.identificationDate,
  status: row.status,
  closedDate: row.status === ActionStatus.Closed ? row.closedDate : null,
  actionsTaken: row.actionsTaken.trim() || null,
  responsiblePerson: row.responsiblePerson.trim() || null,
  identifiedBy: saved?.identifiedBy ?? null,
  potentialDeviation: row.potentialDeviation,
  serious: row.potentialSeriousFinding,
  seriousBreach: row.seriousBreach,
  needsReview: !!saved?.needsReview && !(row.status === ActionStatus.Closed && saved.status !== ActionStatus.Closed),
});

// YYYY-MM-DD(THH…) → dd.MM.yyyy
export const formatDay = (iso: string | null | undefined) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("T")[0].split("-");
  return `${d}.${m}.${y}`;
};

export const ISSUE_FILTERS = ["all", "open", "review", "closed", "serious", "seriousOpen"] as const;
export type IssueFilter = (typeof ISSUE_FILTERS)[number];

export const matchesFilter = (i: SiteIssue, f: IssueFilter): boolean => {
  switch (f) {
    case "open": return i.status === ActionStatus.Open;
    case "review": return i.needsReview;
    case "closed": return i.status === ActionStatus.Closed;
    case "serious": return i.serious || i.seriousBreach;
    case "seriousOpen": return i.status === ActionStatus.Open && (i.serious || i.seriousBreach);
    default: return true;
  }
};

// Nazwy pól z historii (backend: nazwy właściwości C#) po polsku
export const FIELD_LABELS: Record<string, string> = {
  ParticipantNumber: "Nr uczestnika",
  VisitNumber: "Numer wizyty",
  CategoryId: "Czego dotyczy",
  Description: "Opis",
  IdentificationDate: "Data identyfikacji",
  Status: "Status",
  ClosedDate: "Data zamknięcia",
  ActionsTaken: "Podjęte działania",
  ResponsiblePerson: "Osoba odpowiedzialna",
  IdentifiedBy: "Osoba identyfikująca",
  PotentialDeviation: "PD (potencjalne)",
  Serious: "Poważne",
  SeriousBreach: "Serious Breach",
  NeedsReview: "Do weryfikacji",
};

const STATE_KEY: Record<string, keyof SiteIssueState> = {
  ParticipantNumber: "participantNumber",
  VisitNumber: "visitNumber",
  CategoryId: "categoryId",
  Description: "description",
  IdentificationDate: "identificationDate",
  Status: "status",
  ClosedDate: "closedDate",
  ActionsTaken: "actionsTaken",
  ResponsiblePerson: "responsiblePerson",
  IdentifiedBy: "identifiedBy",
  PotentialDeviation: "potentialDeviation",
  Serious: "serious",
  SeriousBreach: "seriousBreach",
  NeedsReview: "needsReview",
};

// Wartość pola po zmianie, do wyświetlenia w historii
export const fieldValue = (state: SiteIssueState | null, field: string, categoryName: (id: number) => string): string => {
  if (!state) return "";
  const key = STATE_KEY[field];
  if (!key) return "";
  const v = state[key];
  if (v === null || v === undefined || v === "") return "—";
  if (key === "status") return v === ActionStatus.Closed ? "zamknięte" : "otwarte";
  if (key === "categoryId") return categoryName(v as number);
  if (key === "identificationDate" || key === "closedDate") {
    const [y, m, d] = String(v).split("T")[0].split("-");
    return `${d}.${m}.${y}`;
  }
  if (typeof v === "boolean") return v ? "tak" : "nie";
  return String(v);
};
